import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Box, SvgIcon, Typography } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { FormProvider, useForm } from "react-hook-form";
import WarningIcon from "@static/icons/Warning.svg";
import { EServiceGroup, NUMBER_OFFER_MAX, WORDPRESS_URL } from "@constants";
import { useAddOffer } from "@hooks/useAddOffer";
import { useOffers } from "@hooks/useOffers";
import { useDeviceType } from "@hooks/useDeviceType";
import { getOrderAuthorization, OrderAuthorizationEnum } from "@utils/getOrderAuthorization";
import { checkCartOfferByServiceGroup } from "@utils/offerUtils";
import { getObjectByPath } from "@utils/getObjectByPath";
import { getHasNestedErrors } from "@utils/getHasNestedErrors";
import "@features/cart/css/cartPage.scss";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { Modal } from "@common/Modal/Modal";
import { WarningBanner } from "@common/WarningBanner/WarningBanner";
import type { IStepRoute } from "@common/StepperRouter";
import type { ICart, ICartOffer, IPortin } from "@features/cart/ICart";
import type { IFlyingCart } from "@features/cart/IFlyingCart";
import { BroadbandCard } from "@features/cart/components/BroadbandCard/BroadbandCard";
import { BroadbandCartAddon } from "@features/cart/components/BroadbandCard/BroadbandCartAddon/BroadbandCartAddon";
import { AddNewSIM } from "@features/cart/components/AddNew/AddNewSIM";
import { FlyingCart } from "@features/cart/components/FlyingCart";
import { MobileRequiredModal } from "@features/cart/components/MobileRequiredModal/MobileRequiredModal";
import { ProductLine } from "@features/cart/components/PortIn/ProductLine";
import { useAddPortin } from "@features/cart/hook/useAddPortin";
import { useClearCart } from "@features/cart/hook/useClearCart";
import { useDeletePortin } from "@features/cart/hook/useDeletePortin";
import { useMobileRequiredModal } from "@features/cart/hook/useMobileRequiredModal";
import { Helmet } from "react-helmet";
import { useStyle } from "./style";
import { useDeleteOffer } from "@features/cart/hook/useDeleteOffer";
import { sendClearCartEvent } from "@features/dataLayer/removeCart";

interface IParams {
    cart: ICart;
    flyingCart: IFlyingCart;
    setCart(cart: ICart | undefined): void;
    nextStep?: IStepRoute;
}

export const CartPage = (props: IParams) => {
    const { cart, flyingCart, setCart, nextStep } = props;

    const [offerIdOfPortInOpen, setOfferIdOfPortInOpen] = useState<number | undefined>();
    const [isPortInCardError, setIsPortInCardError] = useState(false);
    const { addMobileOfferState, addInclusiveEquipmentState, addMobileOffer } = useAddOffer();
    const { clearCartState, callClearCart } = useClearCart();
    const { deletePortinState, deletePortin } = useDeletePortin();
    const { addPortinState, addPortin } = useAddPortin();

    const [orderAuthorization, setOrderAuthorization] = useState<OrderAuthorizationEnum | undefined>(undefined);

    const { push } = useHistory();
    const { classes } = useStyle();
    const [hasConfirmModal, setHasConfirmModal] = useState(false);
    const [isRemovingMobile, setIsRemovingMobile] = useState(false);
    const [hasReachedMaxAllowedOffers, setHasReachedMaxAllowedOffers] = useState(false);
    const [offerToRemove, setOfferToRemove] = useState<ICartOffer>();
    const { isMobile } = useDeviceType();
    const { showMobileRequiredModal, handleAddGoMoMobile } = useMobileRequiredModal({ cart, setCart });
    const { mobileOfferFromCatalog } = useOffers();

    const formMethods = useForm({ shouldUnregister: true });

    useEffect(() => {
        if (cart && cart.nbrAdditionalAllowedMobileSubscriptions < 0) {
            setOrderAuthorization(getOrderAuthorization(cart));
        } else {
            setOrderAuthorization(OrderAuthorizationEnum.OrderOk);
        }
    }, [cart]);

    const displayAddOfferButton = cart && cart.nbrAdditionalAllowedMobileSubscriptions > 0;
    const { t } = useTranslation("cart");

    const backToHome = () => {
        window.location.href = WORDPRESS_URL ?? "https://gomo.ie/";
    };

    const mobileOfferList = useMemo(
        () => cart.offers?.filter((offer) => checkCartOfferByServiceGroup(offer, EServiceGroup.MOBILE)),
        [cart.offers]
    );

    const lastMobileOfferIndex = useMemo(() => {
        let lastIndex = -1;

        cart.offers.forEach((offer, index) => {
            if (checkCartOfferByServiceGroup(offer, EServiceGroup.MOBILE)) {
                lastIndex = index;
            }
        });

        return lastIndex;
    }, [cart.offers]);

    const hasNoMobileSubscription = useMemo(
        () => cart.nbrAdditionalAllowedMobileSubscriptions + (mobileOfferList?.length ?? 0) === NUMBER_OFFER_MAX,
        [cart.nbrAdditionalAllowedMobileSubscriptions, mobileOfferList?.length]
    );

    const isOrderAuthorizationNotOk = useMemo(
        () => orderAuthorization !== undefined && orderAuthorization !== OrderAuthorizationEnum.OrderOk,
        [orderAuthorization]
    );

    const deleteOffer = (cartOfferId: number) => {
        const broadbandOfferList = cart.offers?.filter((offer) =>
            checkCartOfferByServiceGroup(offer, EServiceGroup.SA_BB)
        );
        const hasOneLastMobileOffer = mobileOfferList?.length === 1;
        const selectedOffer = cart.offers.findLast((offer) => offer.offerId === cartOfferId);
        const isMobileOffer = checkCartOfferByServiceGroup(selectedOffer, EServiceGroup.MOBILE);
        setIsRemovingMobile(isMobileOffer);

        if (hasOneLastMobileOffer && isMobileOffer && broadbandOfferList.length > 0 && hasNoMobileSubscription) {
            setHasConfirmModal(true);

            return;
        }

        const hasBroadbandAddon = broadbandOfferList.some((offer) => offer.addOns.length > 0);
        if (!isMobileOffer && broadbandOfferList && hasBroadbandAddon) {
            setHasConfirmModal(true);
            setOfferToRemove(selectedOffer);

            return;
        }

        if (selectedOffer) {
            removeOfferFromCart(selectedOffer);
        }
    };
    const { error: deleteOfferError, handleDeleteOffer, isLoading: deleteOfferIsLoading } = useDeleteOffer({ setCart });

    const removeOfferFromCart = (offer: ICartOffer) => {
        setHasConfirmModal(false);
        handleDeleteOffer(offer).then((newCart) => {
            setOfferIdOfPortInOpen(undefined);

            if (newCart?.offers.length === 0) {
                backToHome();
            }
        });
    };

    const clearCart = () => {
        callClearCart().then(() => {
            sendClearCartEvent(cart);
            setCart(undefined);
            backToHome();
        });
    };

    const addOfferCart = () => {
        const mobileCatalogOfferCode = mobileOfferFromCatalog?.catalogOfferCode;
        if (mobileCatalogOfferCode) {
            addMobileOffer(mobileCatalogOfferCode).then((cartResult) => {
                if (cartResult) {
                    setCart(cartResult);
                    setOfferIdOfPortInOpen(undefined);
                }
            });
        }
    };

    const addPortinToCart = (cartOfferId: number, portinForm: IPortin, closePortInForm?: () => void) => {
        addPortin(cartOfferId, portinForm).then((cartResult) => {
            if (cartResult) {
                setCart(cartResult);
                setOfferIdOfPortInOpen(undefined);
                closePortInForm?.();
            }
        });
    };

    const deletePortinCart = (cartOfferId: number) => {
        deletePortin(cartOfferId).then((cartResult) => {
            if (cartResult) {
                setCart(cartResult);
                setOfferIdOfPortInOpen(undefined);
            }
        });
    };

    const hasTooManyBroadbandOffers = cart.nbrAdditionalAllowedBroadbandSubscriptions < 0;

    const onValidNext = () => {
        if (showMobileRequiredModal) {
            return;
        }
        if (isOrderAuthorizationNotOk || hasTooManyBroadbandOffers) {
            setHasReachedMaxAllowedOffers(true);

            return;
        }

        setHasReachedMaxAllowedOffers(false);
        if (nextStep) {
            push({ pathname: nextStep.path });
        }
    };

    const isPortInOpen = offerIdOfPortInOpen !== undefined;

    const updatePortInCardError = () => setIsPortInCardError(isPortInOpen);

    const handleValidSubmit = () => {
        updatePortInCardError();
        if (!isPortInOpen) {
            onValidNext();
        }
    };

    const handleNextClick = () => {
        formMethods.handleSubmit(handleValidSubmit, updatePortInCardError)();
    };

    const isLoading =
        addMobileOfferState.isLoading ||
        addInclusiveEquipmentState.isLoading ||
        clearCartState.isLoading ||
        deleteOfferIsLoading ||
        deletePortinState.isLoading ||
        addPortinState.isLoading;

    const error =
        addMobileOfferState.error ??
        addInclusiveEquipmentState.error ??
        clearCartState.error ??
        deleteOfferError ??
        deletePortinState.error ??
        addPortinState.error;

    const renderOffer = (offer: ICartOffer) => {
        if (checkCartOfferByServiceGroup(offer, EServiceGroup.SA_BB) && offer.prequal) {
            return (
                <div key={offer.offerId}>
                    <BroadbandCard
                        offerId={offer.offerId}
                        offerName={offer.baseOffer.description}
                        prequalId={offer.prequal.prequalId}
                        services={offer.prequal.services || []}
                        setCart={setCart}
                        onDelete={() => deleteOffer(offer.offerId)}
                    />
                    <BroadbandCartAddon cartAddonList={offer.addOns} offerId={offer.offerId} setCart={setCart} />
                </div>
            );
        }

        const hasEsimErrors = getHasNestedErrors(
            getObjectByPath(formMethods.formState.errors, `eSim.${offer.offerId}`)
        );

        return (
            <div key={offer.offerId}>
                <ProductLine
                    addPortin={addPortinToCart}
                    deleteOffer={deleteOffer}
                    deletePortin={deletePortinCart}
                    hasError={hasEsimErrors}
                    isPortInCardError={isPortInCardError}
                    isPortOpen={offerIdOfPortInOpen === offer.offerId}
                    setCart={setCart}
                    setIsPortInCardError={setIsPortInCardError}
                    setIsPortOpen={(isPortOpen: boolean) => {
                        if (isPortOpen) {
                            setOfferIdOfPortInOpen(offer.offerId);
                        } else {
                            setOfferIdOfPortInOpen(undefined);
                        }
                        setIsPortInCardError(false);
                    }}
                    userOffer={offer}
                    onPortInDisabled={(disabledOfferId) => {
                        if (offerIdOfPortInOpen === disabledOfferId) {
                            setOfferIdOfPortInOpen(undefined);
                        }
                        setIsPortInCardError(false);
                    }}
                />
            </div>
        );
    };

    const hasOneBroadbandOffer = cart.offers?.some((offer) => checkCartOfferByServiceGroup(offer, EServiceGroup.SA_BB));

    return (
        <FormProvider {...formMethods}>
            <div className={classes.cartPage}>
                <Helmet>
                    <meta content={t("pageTitle.cart")} property="og:title" />
                    <meta content="noindex, nofollow" name="robots" />
                    <title>{t("pageTitle.cart")}</title>
                </Helmet>
                <HandleErrorAndLoading error={error} isLoading={isLoading} retryFetch={() => window.location.reload()}>
                    {cart && (
                        <Box display="flex" gap={4}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={2}
                                maxWidth={isMobile ? "100%" : 700}
                                width="100%"
                            >
                                <Box display="flex" flexDirection="column" gap={5}>
                                    {((hasOneBroadbandOffer && hasTooManyBroadbandOffers) ||
                                        isOrderAuthorizationNotOk) && (
                                        <Box display="flex" flexDirection="column" gap={2}>
                                            {hasOneBroadbandOffer && hasTooManyBroadbandOffers && (
                                                <WarningBanner
                                                    content={t("needToDeleteBroadbandOffer")}
                                                    hasError={hasReachedMaxAllowedOffers}
                                                    icon={<WarningIcon />}
                                                    isAlert
                                                />
                                            )}
                                            {isOrderAuthorizationNotOk && (
                                                <WarningBanner
                                                    content={t("needToDeleteOffer", {
                                                        max: NUMBER_OFFER_MAX,
                                                        numberOfferToDelete:
                                                            cart.nbrAdditionalAllowedMobileSubscriptions * -1,
                                                    })}
                                                    hasError={hasReachedMaxAllowedOffers}
                                                    icon={<WarningIcon />}
                                                    isAlert
                                                />
                                            )}
                                        </Box>
                                    )}
                                    {cart.offers.map((offer, index) => (
                                        <Box display="flex" flexDirection="column" gap={2} key={offer.offerId}>
                                            {renderOffer(offer)}
                                            {displayAddOfferButton &&
                                                index === lastMobileOfferIndex &&
                                                lastMobileOfferIndex >= 0 && (
                                                    <div className="cart-action">
                                                        <AddNewSIM id="xtest_addOffer" onClick={addOfferCart} />
                                                    </div>
                                                )}
                                        </Box>
                                    ))}
                                    {displayAddOfferButton && lastMobileOfferIndex < 0 && (
                                        <div className="cart-action">
                                            <AddNewSIM id="xtest_addOffer" onClick={addOfferCart} />
                                        </div>
                                    )}
                                </Box>
                                <Modal
                                    close={() => setHasConfirmModal(false)}
                                    confirm={() => {
                                        if (!isRemovingMobile && offerToRemove) {
                                            removeOfferFromCart(offerToRemove);
                                        } else {
                                            clearCart();
                                        }
                                    }}
                                    confirmLabel={
                                        isRemovingMobile
                                            ? t("clearCart.mobile.confirm")
                                            : t("clearCart.broadband.confirm")
                                    }
                                    displayCancel
                                    isModalOpen={hasConfirmModal}
                                    maxWidth="xs"
                                    title={
                                        isRemovingMobile ? t("clearCart.mobile.title") : t("clearCart.broadband.title")
                                    }
                                >
                                    <Box alignItems="flex-start" columnGap={4} display="flex" width="100%">
                                        <SvgIcon>
                                            <Delete color="secondary" />
                                        </SvgIcon>
                                        <Typography color="secondary" variant="normal">
                                            {isRemovingMobile
                                                ? t("clearCart.mobile.message")
                                                : t("clearCart.broadband.message")}
                                        </Typography>
                                    </Box>
                                </Modal>
                                <MobileRequiredModal
                                    isOpen={showMobileRequiredModal}
                                    onAddMobile={handleAddGoMoMobile}
                                />
                            </Box>
                            {flyingCart && (
                                <FlyingCart
                                    arePricesLoading={showMobileRequiredModal}
                                    flyingCart={flyingCart}
                                    nextStep={nextStep}
                                    onNextClick={handleNextClick}
                                />
                            )}
                        </Box>
                    )}
                </HandleErrorAndLoading>
            </div>
        </FormProvider>
    );
};
